import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { Box, Button, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
// routes
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
// hooks
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import { getAuth, signOut } from 'firebase/auth';
import Cookies from 'js-cookie';
import { aclsLocalStorageKey } from 'src/careflow/ph-core/admin/permissions/permissions.api';
import usePatientTabsStore from 'src/stores/patient-tabs';
import { getBackEndBaseUrl } from 'src/utils/domain-utils';
import MenuPopover from '../../../components/MenuPopover';
import MyAvatar from '../../../components/MyAvatar';
import { IconButtonAnimate } from '../../../components/animate';
import { SimpleOrgI } from 'src/careflow/ph-core/authentication/context/JWTContext';
import { AuthService } from 'src/careflow/ph-core/authentication/service/AuthService';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Home',
    linkTo: PATH_DASHBOARD.general.app,
  },
  // {
  //   label: 'Profile',
  //   linkTo: PATH_DASHBOARD.user.account,
  // },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const patientTabStore = usePatientTabsStore();

  const { user, logout, getSelectedOrg } = useAuth();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const [enableLogoutFromAllApps, setEnableLogoutFromAllApps] = useState<boolean>(false);

  const refreshToken = Cookies.get('refreshToken');

  async function loadLogoutFAASetting() {
    const response = await AuthService.isLogoutFromAllAppsEnabled();
    setEnableLogoutFromAllApps(response.enable_logout_faa);
  }

  const selectedOrg = useMemo<SimpleOrgI>(() => getSelectedOrg(), [getSelectedOrg]);

  useEffect(() => {
    loadLogoutFAASetting();
  }, [user]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.logout, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const logoutFromAllAppsElement = (
    <div>
      <Divider sx={{ borderStyle: 'dashed' }} />

      <MenuItem sx={{ m: 1 }}>
        <form
          onSubmit={() => {
            localStorage.removeItem(aclsLocalStorageKey);
            patientTabStore.closeAllPatients();
            patientTabStore.closePatient(patientTabStore.currentPatientId);
            signOut(getAuth()).catch((error) => {
              console.log('Error Logging Out');
            });

            return true;
          }}
          action={`${getBackEndBaseUrl()}/auth/logout-from-all-apps`}
          method="post"
        >
          <input type="hidden" name="refresh_token" value={refreshToken} />
          <Button type="submit" variant="text" size="small">
            Logout From All Apps
          </Button>
        </form>
      </MenuItem>
    </div>
  );

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
          width: 300
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Stack>
            <Stack direction={'row'}>
              <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-wrap' }}>
                {'User: '}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {user?.displayName}
              </Typography>
            </Stack>

            <Stack direction={'row'}>
              <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-wrap' }}>
                {'Email: '}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {user?.email}
              </Typography>
            </Stack>

            <Stack direction={'row'}>
              <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-wrap' }}>
                {'Org: '}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {selectedOrg?.name ?? ''}
              </Typography>
            </Stack>

            <Stack direction={'row'}>
              <Typography variant="subtitle2" sx={{ whiteSpace: 'pre-wrap' }}>
                {'Role: '}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                {user?.roleName}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        {enableLogoutFromAllApps && logoutFromAllAppsElement}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem sx={{ m: 1 }}>
          <form
            onSubmit={() => {
              localStorage.removeItem(aclsLocalStorageKey);
              patientTabStore.closeAllPatients();
              patientTabStore.closePatient(patientTabStore.currentPatientId);
              signOut(getAuth()).catch((error) => {
                console.log('Error Logging Out');
              });

              return true;
            }}
            action={`${getBackEndBaseUrl()}/auth/logout-handler`}
            method="post"
          >
            <input type="hidden" name="refresh_token" value={refreshToken} />
            <Button type="submit" variant="text" size="small">
              Logout
            </Button>
          </form>
        </MenuItem>
      </MenuPopover>
    </>
  );
}
